import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { animated, useSpring } from "@react-spring/web";
import useMeasure from "react-use-measure";
import styles from "./common.module.scss";
import { ChartView } from "src/types/experiences/leadsAndReports";

const VIEWPORTS: { type: ChartView; icon: string }[] = [
  { type: "table_view", icon: "paperflite-table" },
  { type: "graph_view", icon: "paperflite-graph" },
  { type: "pie_chart_view", icon: "paperflite-pie-chart" },
];

interface ViewToggleProps {
  current: ChartView;
  disableView?: ChartView[];
  // eslint-disable-next-line no-unused-vars
  onChange: (view: ChartView) => void;
}

export const ViewToggle: React.FC<ViewToggleProps> = ({ current, disableView = [], onChange }) => {
  const views = VIEWPORTS.filter((v) => !disableView.includes(v.type));

  const [boundsRef, bounds] = useMeasure();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const hasMeasured = indicatorStyle.width > 0;

  const springProps = useSpring({
    to: hasMeasured ? { left: indicatorStyle.left, width: indicatorStyle.width } : undefined,
    config: { tension: 300, friction: 30 },
  });

  useEffect(() => {
    const selectedIndex = views.findIndex((v) => v.type === current);
    const selectedEl = itemRefs.current[selectedIndex];
    if (!selectedEl) return;

    const itemBounds = selectedEl.getBoundingClientRect();
    const containerBounds = selectedEl.parentElement?.getBoundingClientRect();
    if (containerBounds) {
      setIndicatorStyle({
        left: itemBounds.left - containerBounds.left,
        width: itemBounds.width,
      });
    }
  }, [current, bounds, views.length]);

  return (
    <div ref={boundsRef} className={styles.viewToggle}>
      {hasMeasured && <animated.div className={styles.viewToggleIndicator} style={springProps} />}
      {views.map((v, index) => (
        <div
          key={v.type}
          ref={(el) => {
            itemRefs.current[index] = el;
          }}
          className={cx(styles.viewToggleItem, { [styles.active]: current === v.type })}
          onClick={() => onChange(v.type)}
        >
          <i className={v.icon} />
        </div>
      ))}
    </div>
  );
};
